"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LinkCrmReceiptActionView = exports.CrmReceiptActionRequestBody = exports.LinkCrmReceiptProblem = exports.LinkCrmReceiptActionResult = exports.CrmReceiptActionOutcome = exports.LinkCrmReceiptAction = exports.CrmReceiptActionKind = exports.CRM_RECEIPT_NEVER_RELEASABLE_KEYS = exports.LINK_CRM_RECEIPT_PROBLEMS_MAX = exports.LINK_CRM_RECEIPT_ACTIONS_MAX = exports.CRM_RECEIPT_ACTIONS_CAPABILITY = void 0;
exports.crmReceiptActionAllowed = crmReceiptActionAllowed;
const zod_1 = require("zod");
/**
 * RL-990 — an operator releases a held CRM receipt, or requeues a rejected
 * one, on the store's Link (the outbox of carded receipts, RL-587).
 *
 * Igoris, 2026-10-26 (option A):
 * - any hold may be released EXCEPT the binding / target-changed group: the
 *   binding is immutable, so a release would send to a changed target. Stricter
 *   than asked (Platform, 2026-10-26): `osr_order_already_sold` too — it is
 *   decided once, at upload, and a released row would be a second sale of an
 *   online order already sold;
 * - a row whose counting write has an unknown outcome (`writeUncertain`) is
 *   released only through the Link's own purchase-by-key lookup: not found —
 *   sent; found — kept held, never sent again (`found_in_crm`); no answer —
 *   unchanged (`lookup_failed`);
 * - one operator with the dedicated grant and a fresh login, audited;
 * - a rejected row gets ONE more try after a CRM fix (card, product);
 * - no "close": nothing moves a row to a final state without the CRM.
 *
 * Back Office hands actions over in the answer to the Link's status report,
 * only to a Link that advertises [CRM_RECEIPT_ACTIONS_CAPABILITY]; the Link
 * answers each in a later report. The outer arrays are loose (only the bound
 * is checked), the elements strict and parsed one by one, as VL-400's.
 */
/** The capability a Link advertises in `LinkStatusReport.capabilities`. */
exports.CRM_RECEIPT_ACTIONS_CAPABILITY = 'crm_receipt_actions_v1';
/** Most actions in one answer; a report may carry twice as many results. */
exports.LINK_CRM_RECEIPT_ACTIONS_MAX = 50;
/** Most held / rejected rows a report lists, the oldest first. */
exports.LINK_CRM_RECEIPT_PROBLEMS_MAX = 200;
const ActionId = zod_1.z.string().min(1).max(128);
/**
 * A stable code, the alphabet of `LinkCrmErrorKey` (`link.ts`): a CRM message,
 * which can quote a phone number, cannot pass through it.
 */
const CrmReceiptKey = zod_1.z
    .string()
    .max(64)
    .regex(/^[A-Za-z][A-Za-z0-9_.-]*$/, 'a stable error code, not free text');
/**
 * Hold reasons an operator can never release. The first nine are the binding
 * / target-changed group; the last is RL-990's stricter reading.
 */
exports.CRM_RECEIPT_NEVER_RELEASABLE_KEYS = [
    'binding_damaged',
    'binding_offline_handoff_unbound',
    'crm_purchase_binding_absent',
    'crm_purchase_target_changed',
    'crm_purchase_unowned',
    'target_changed',
    'target_unbound',
    'original_target_changed',
    'source_changed_before_send',
    'osr_order_already_sold',
];
const NEVER_RELEASABLE = new Set(exports.CRM_RECEIPT_NEVER_RELEASABLE_KEYS);
/**
 * Whether an operator may act on a row in [status] with [errorKey]: release a
 * held one, requeue a rejected one. The Link decides again on the row as it
 * stands when the action arrives.
 */
function crmReceiptActionAllowed(status, errorKey) {
    if (status === 'rejected')
        return true;
    return errorKey === null || !NEVER_RELEASABLE.has(errorKey);
}
exports.CrmReceiptActionKind = zod_1.z.enum(['release', 'requeue']);
/**
 * One action, in `LinkStatusResponse.crmReceiptActions`. It names the row by
 * its `documentId` and the status and key the operator saw: the Link refuses
 * it (`not_applicable`) when either has changed since.
 */
exports.LinkCrmReceiptAction = zod_1.z
    .object({
    id: ActionId,
    kind: exports.CrmReceiptActionKind,
    documentId: ActionId,
    expectedStatus: zod_1.z.enum(['held', 'rejected']),
    expectedErrorKey: CrmReceiptKey.nullable(),
    requestedAt: zod_1.z.string().datetime(),
})
    .strict()
    .refine((action) => (action.kind === 'release' && action.expectedStatus === 'held') ||
    (action.kind === 'requeue' && action.expectedStatus === 'rejected'), 'release names a held row, requeue a rejected one');
/**
 * What the Link did:
 * - `released` / `requeued` — the row is `pending` again and goes the usual
 *   way (an uncertain one only after the lookup did not find it);
 * - `found_in_crm` — the lookup found the purchase: the row stays held and
 *   is never sent again (it may have counted);
 * - `lookup_failed` — the lookup got no answer (or the connector is not
 *   running): nothing changed, the action may be asked again;
 * - `not_applicable` — no such row, or its status or key is no longer the one
 *   the operator saw;
 * - `not_releasable` — the row's hold is one no operator may release.
 */
exports.CrmReceiptActionOutcome = zod_1.z.enum([
    'released',
    'requeued',
    'found_in_crm',
    'lookup_failed',
    'not_applicable',
    'not_releasable',
]);
/** One answer, in `LinkStatusReport.crmReceiptActionResults`. */
exports.LinkCrmReceiptActionResult = zod_1.z
    .object({ actionId: ActionId, outcome: exports.CrmReceiptActionOutcome })
    .strict();
/**
 * One held or rejected row, in `LinkCrmReceiptQueue.problems`: what an
 * operator needs to choose it. Never a card, a receipt body or anything the
 * CRM said (`errorKey` is a code; a key outside the alphabet is sent as null).
 */
exports.LinkCrmReceiptProblem = zod_1.z
    .object({
    documentId: ActionId,
    terminalId: ActionId,
    receiptType: zod_1.z.enum(['sale', 'refund', 'void']),
    status: zod_1.z.enum(['held', 'rejected']),
    errorKey: CrmReceiptKey.nullable(),
    writeUncertain: zod_1.z.boolean(),
    attempts: zod_1.z.number().int().min(0),
    documentAt: zod_1.z.string().datetime(),
    lastAttemptAt: zod_1.z.string().datetime().nullable(),
})
    .strict();
// ------------------------------------------------------------------
// Back Office API: `POST /api/v1/connectors/locations/:locationId/link-status/crm-receipts/actions`
// ------------------------------------------------------------------
/**
 * An operator's request: one row of the Link's latest fresh report, and what
 * to do with it. Back Office fills in the status and key the report showed.
 */
exports.CrmReceiptActionRequestBody = zod_1.z
    .object({ documentId: ActionId, kind: exports.CrmReceiptActionKind })
    .strict();
/** One action as the operator sees it (`LinkStatusView.crmReceiptActions`). */
exports.LinkCrmReceiptActionView = zod_1.z.object({
    id: zod_1.z.string(),
    kind: exports.CrmReceiptActionKind,
    documentId: zod_1.z.string(),
    expectedStatus: zod_1.z.enum(['held', 'rejected']),
    expectedErrorKey: zod_1.z.string().nullable(),
    requestedAt: zod_1.z.string().datetime(),
    /** The operator's display name; null once the account is gone. */
    requestedBy: zod_1.z.string().nullable(),
    /** Last time Back Office handed it to the Link; null = not collected yet. */
    deliveredAt: zod_1.z.string().datetime().nullable(),
    completedAt: zod_1.z.string().datetime().nullable(),
    outcome: exports.CrmReceiptActionOutcome.nullable(),
});
//# sourceMappingURL=link-crm-receipt-actions.js.map